import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Gallery({ images = [], testid = "gallery" }) {
  const [index, setIndex] = useState(-1);
  const open = index >= 0;

  const close = useCallback(() => setIndex(-1), []);
  const prev = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i - 1 + images.length) % images.length);
    },
    [images.length]
  );
  const next = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i + 1) % images.length);
    },
    [images.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, prev, next]);

  if (!images.length) return null;

  return (
    <>
      <div
        data-testid={testid}
        className="columns-2 md:columns-3 gap-4 md:gap-5 [column-fill:_balance]"
      >
        {images.map((src, i) => (
          <button
            key={src + i}
            data-testid={`${testid}-item-${i}`}
            onClick={() => setIndex(i)}
            className="group mb-4 md:mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-slate-100 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/30"
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
            />
          </button>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`${testid}-lightbox`}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 backdrop-blur-sm p-4 md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <button
              onClick={close}
              data-testid={`${testid}-lightbox-close`}
              className="absolute top-5 right-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Închide"
            >
              <X className="h-6 w-6" />
            </button>

            {images.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-3 md:left-8 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="h-7 w-7" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-3 md:right-8 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  aria-label="Următor"
                >
                  <ChevronRight className="h-7 w-7" />
                </button>
              </>
            )}

            <motion.img
              key={index}
              src={images[index]}
              alt=""
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
